import { useState, type ReactNode } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import libraryUploadCopy from '@/i18n/ns/library-upload';
import { WorkspaceIcon } from '../icons';
import { useSessionPicture } from '../apps/session-picture';
import { useIntlLocale } from '../intl-locale';
import { FilePageIcon, KindPageIcon } from './file-page-icon';
import type { LibraryUpload } from './upload-queue';
import { durationLabel, progressSizes, sizeLabel } from './upload-format';
import type { LibraryFeedItem } from './api';

type UploadCopy = (typeof libraryUploadCopy)['en'];

/** One row of the Library list, the same for every kind: page icon, name, a grey line under it, then privacy and time. */
export function LibraryItemRow({
  id,
  kind,
  name,
  picture = null,
  meta,
  hint,
  audience,
  time,
  arrived = false,
  action,
  onOpen,
}: {
  id: string;
  kind: LibraryFeedItem['kind'];
  name: string;
  /** A picture of the work (a deck's first slide, a Result's Overview) as an image URL, in the icon's place. */
  picture?: string | null;
  meta: ReactNode;
  /** A quiet note after the meta line ("Not in this filter"). */
  hint?: string | null;
  /** The lock's name when private ("Private"); else the audience it is shared with. */
  audience: { private: boolean; label: string };
  time: string | null;
  /** Just uploaded: a brief tint that fades. */
  arrived?: boolean;
  action?: ReactNode;
  onOpen: () => void;
}) {
  // A picture that can't be read (or, in the native apps, is still being read) keeps the kind's page icon.
  const { src: shown } = useSessionPicture(picture);
  const [failed, setFailed] = useState<string | null>(null);
  return (
    <li className="ws-library-item" data-arrived={arrived || undefined} data-kind={kind}>
      {shown && failed !== shown ? (
        <span className="ws-library-thumb" aria-hidden="true">
          <img src={shown} alt="" loading="lazy" onError={() => setFailed(shown)} />
        </span>
      ) : kind === 'file' ? (
        <FilePageIcon name={name} />
      ) : (
        <KindPageIcon kind={kind} />
      )}
      <div className="ws-library-item-body">
        <div className="ws-library-item-text">
          <button
            type="button"
            className="ws-library-item-open"
            data-id={id}
            data-testid="library-row"
            onClick={onOpen}
          >
            {name}
          </button>
          <span className="ws-library-item-meta">
            {meta}
            {/* On a phone the time joins this line; the right side keeps only the lock. */}
            {time && <span className="ws-library-item-meta-time"> · {time}</span>}
            {hint && <span className="ws-library-item-hint">{hint}</span>}
          </span>
        </div>
        <div className="ws-library-item-trail">
          {action && <div className="ws-library-item-action">{action}</div>}
          <span className="ws-library-item-audience" role="img" aria-label={audience.label}>
            <WorkspaceIcon name={audience.private ? 'lock' : 'users'} size={14} />
          </span>
          {time && <span className="ws-library-item-time">{time}</span>}
        </div>
      </div>
    </li>
  );
}

function problemText(upload: LibraryUpload, copy: UploadCopy, locale: string) {
  const problem = upload.problem;
  if (!problem) return '';
  if (problem.kind === 'type') {
    const extension = problem.extension.toLowerCase();
    if (problem.modern)
      return formatCopy(copy.unsupportedOld, { extension, modern: problem.modern });
    return extension ? formatCopy(copy.unsupported, { extension }) : copy.unsupportedUnnamed;
  }
  if (problem.kind === 'size')
    return formatCopy(copy.tooLarge, { size: sizeLabel(problem.bytes, locale) });
  if (problem.kind === 'empty') return copy.empty;
  return problem.message;
}

/** "2.9 of 4.8 MB · About 6 seconds left", "Waiting to upload", "Reading the file…". */
export function uploadStatusText(upload: LibraryUpload, copy: UploadCopy, locale: string) {
  if (upload.status === 'waiting') return copy.waiting;
  if (upload.status === 'finishing') return copy.finishing;
  if (upload.status === 'paused') return copy.paused;
  if (upload.status === 'problem') return problemText(upload, copy, locale);
  const sizes = formatCopy(copy.progress, progressSizes(upload.loaded, upload.size, locale));
  return upload.secondsLeft === null
    ? sizes
    : `${sizes} · ${formatCopy(copy.timeLeft, { time: durationLabel(upload.secondsLeft, locale) })}`;
}

/**
 * A file on its way: the same row as a saved file, with a thin progress bar under its name and the numbers to the
 * right, then a round cancel button. A problem shows in the row itself, with what can be done about it.
 */
export function LibraryUploadRow({
  upload,
  onRemove,
  onRetry,
}: {
  upload: LibraryUpload;
  onRemove: () => void;
  onRetry: () => void;
}) {
  const copy = useNamespaceCopy(libraryUploadCopy);
  const locale = useIntlLocale();
  const status = uploadStatusText(upload, copy, locale);
  const moving = upload.status !== 'problem';
  const percent = upload.size > 0 ? Math.round((upload.loaded / upload.size) * 100) : 0;
  const retryable =
    upload.status === 'paused' ||
    (upload.status === 'problem' && upload.problem?.kind === 'failed');
  return (
    <li
      className="ws-library-item ws-library-upload"
      data-status={upload.status}
      data-testid="library-upload-row"
    >
      <FilePageIcon name={upload.name} />
      <div className="ws-library-item-body">
        <div className="ws-library-upload-main">
          <strong className="ws-library-upload-name">{upload.name}</strong>
          {moving && (
            <span
              className="ws-library-progress"
              role="progressbar"
              aria-label={formatCopy(copy.uploadingName, { name: upload.name })}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={percent}
              aria-valuetext={status}
            >
              <span style={{ transform: `scaleX(${percent / 100})` }} />
            </span>
          )}
          <span
            className="ws-library-upload-status"
            data-problem={upload.status === 'problem' || undefined}
            data-testid="library-upload-status"
          >
            {upload.status === 'problem' && <WorkspaceIcon name="alert" size={14} />}
            {status}
          </span>
        </div>
        <div className="ws-library-upload-actions">
          {retryable && (
            <button
              type="button"
              className="pc-btn"
              data-variant="secondary"
              data-size="sm"
              aria-label={formatCopy(copy.retryName, { name: upload.name })}
              onClick={onRetry}
            >
              {copy.retry}
            </button>
          )}
          {upload.status === 'paused' || upload.status === 'problem' ? (
            <button
              type="button"
              className="pc-btn"
              data-variant="quiet"
              data-size="sm"
              aria-label={formatCopy(copy.removeName, { name: upload.name })}
              onClick={onRemove}
            >
              {copy.remove}
            </button>
          ) : (
            upload.status !== 'finishing' && (
              <button
                type="button"
                className="ws-library-upload-cancel"
                aria-label={formatCopy(copy.cancelName, { name: upload.name })}
                data-testid="library-upload-cancel"
                onClick={onRemove}
              >
                <WorkspaceIcon name="close" size={14} />
              </button>
            )
          )}
        </div>
      </div>
    </li>
  );
}
